import { DefaultTheme, NoteDisplayMode } from '@blocksuite/affine-model';
import type { ServiceProvider } from '@blocksuite/global/di';
import {
  type AssetsManager,
  ASTWalker,
  BaseAdapter,
  type BlockSnapshot,
  BlockSnapshotSchema,
  type DocSnapshot,
  type ExtensionType,
  type FromBlockSnapshotPayload,
  type FromBlockSnapshotResult,
  type FromDocSnapshotPayload,
  type FromDocSnapshotResult,
  type FromSliceSnapshotPayload,
  type FromSliceSnapshotResult,
  nanoid,
  type SliceSnapshot,
  type ToBlockSnapshotPayload,
  type ToDocSnapshotPayload,
  type Transformer,
} from '@blocksuite/store';
import type { Root } from 'mdast';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import remarkStringify from 'remark-stringify';
import { unified } from 'unified';

import { type AdapterContext, AdapterFactoryIdentifier } from '../types';
import {
  type BlockMarkdownAdapterMatcher,
  BlockMarkdownAdapterMatcherIdentifier,
} from './block-adapter';
import {
  InlineDeltaToMarkdownAdapterMatcherIdentifier,
  MarkdownASTToDeltaMatcherIdentifier,
  MarkdownDeltaConverter,
} from './delta-converter';
import { remarkGfm } from './gfm';
import { MarkdownPreprocessorManager } from './preprocessor';
import { remarkCallout } from './remark-plugins/remark-callout';
import { remarkUnwrapImages } from './remark-plugins/remark-unwrap-images';
import type { Markdown, MarkdownAST } from './type';

type BlockMarkdownMap = {
  blockId: string;
  startLine: number; // 1-based
  endLine: number; // 1-based, inclusive
  markdown: string; // 该 block 在最终文件中的 markdown 片段
};

type MarkdownToSliceSnapshotPayload = {
  file: Markdown;
  assets?: AssetsManager;
  workspaceId: string;
  pageId: string;
};

// 新增：块层级结构类型
type BlockHierarchy = {
  block: BlockSnapshot;
  children: BlockHierarchy[];
  level: number;
};

export class MarkdownAdapter extends BaseAdapter<Markdown> {
  private readonly _traverseMarkdown = (
    markdown: MarkdownAST,
    snapshot: BlockSnapshot,
    assets?: AssetsManager,
    template?: boolean
  ) => {
    const walker = new ASTWalker<MarkdownAST, BlockSnapshot>();
    walker.setONodeTypeGuard(
      (node): node is MarkdownAST =>
        !Array.isArray(node) &&
        'type' in (node as object) &&
        (node as MarkdownAST).type !== undefined
    );
    walker.setEnter(async (o, context) => {
      for (const matcher of this.blockMatchers) {
        if (matcher.toMatch(o)) {
          const adapterContext: AdapterContext<
            MarkdownAST,
            BlockSnapshot,
            MarkdownDeltaConverter
          > = {
            walker,
            walkerContext: context,
            configs: this.configs,
            job: this.job,
            deltaConverter: this.deltaConverter,
            provider: this.provider,
            textBuffer: { content: '' },
            assets,
            template,
          };
          await matcher.toBlockSnapshot.enter?.(o, adapterContext);
        }
      }
    });
    walker.setLeave(async (o, context) => {
      for (const matcher of this.blockMatchers) {
        if (matcher.toMatch(o)) {
          const adapterContext: AdapterContext<
            MarkdownAST,
            BlockSnapshot,
            MarkdownDeltaConverter
          > = {
            walker,
            walkerContext: context,
            configs: this.configs,
            job: this.job,
            deltaConverter: this.deltaConverter,
            provider: this.provider,
            textBuffer: { content: '' },
            assets,
          };
          await matcher.toBlockSnapshot.leave?.(o, adapterContext);
        }
      }
    });
    return walker.walk(markdown, snapshot);
  };

  private readonly _traverseSnapshot = async (
    snapshot: BlockSnapshot,
    markdown: MarkdownAST,
    assets?: AssetsManager,
    template?: boolean
  ) => {
    const assetsIds: string[] = [];
    const walker = new ASTWalker<BlockSnapshot, MarkdownAST>();
    walker.setONodeTypeGuard(
      (node): node is BlockSnapshot =>
        BlockSnapshotSchema.safeParse(node).success
    );
    walker.setEnter(async (o, context) => {
      for (const matcher of this.blockMatchers) {
        if (matcher.fromMatch(o)) {
          const adapterContext: AdapterContext<
            BlockSnapshot,
            MarkdownAST,
            MarkdownDeltaConverter,
            boolean
          > = {
            walker,
            walkerContext: context,
            configs: this.configs,
            job: this.job,
            deltaConverter: this.deltaConverter,
            provider: this.provider,
            textBuffer: { content: '' },
            assets,
            updateAssetIds: (assetsId: string) => {
              assetsIds.push(assetsId);
            },
            template,
          };
          await matcher.fromBlockSnapshot.enter?.(o, adapterContext);
        }
      }
    });
    walker.setLeave(async (o, context) => {
      for (const matcher of this.blockMatchers) {
        if (matcher.fromMatch(o)) {
          const adapterContext: AdapterContext<
            BlockSnapshot,
            MarkdownAST,
            MarkdownDeltaConverter
          > = {
            walker,
            walkerContext: context,
            configs: this.configs,
            job: this.job,
            deltaConverter: this.deltaConverter,
            provider: this.provider,
            textBuffer: { content: '' },
            assets,
          };
          await matcher.fromBlockSnapshot.leave?.(o, adapterContext);
        }
      }
    });
    return {
      ast: (await walker.walk(snapshot, markdown)) as Root,
      assetsIds,
    };
  };

  deltaConverter: MarkdownDeltaConverter;
  preprocessorManager: MarkdownPreprocessorManager;

  readonly blockMatchers: BlockMarkdownAdapterMatcher[];

  constructor(job: Transformer, provider: ServiceProvider) {
    super(job, provider);
    const blockMatchers = Array.from(
      provider.getAll(BlockMarkdownAdapterMatcherIdentifier).values()
    );
    const inlineDeltaToMarkdownAdapterMatchers = Array.from(
      provider.getAll(InlineDeltaToMarkdownAdapterMatcherIdentifier).values()
    );
    const markdownInlineToDeltaMatchers = Array.from(
      provider.getAll(MarkdownASTToDeltaMatcherIdentifier).values()
    );
    this.blockMatchers = blockMatchers;
    this.deltaConverter = new MarkdownDeltaConverter(
      job.adapterConfigs,
      inlineDeltaToMarkdownAdapterMatchers,
      markdownInlineToDeltaMatchers
    );
    this.preprocessorManager = new MarkdownPreprocessorManager(provider);
  }

  private _astToMarkdown(ast: Root) {
    const out = unified()
      .use(remarkGfm)
      .use(remarkStringify, {
        resourceLink: true,
      })
      .use(remarkMath, { singleDollarTextMath: false })
      .stringify(ast)
      .replace(/&#x20;\n/g, ' \n');
    return this._normalizeEscape(out);
  }

  private _markdownToAst(markdown: Markdown) {
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkMath, { singleDollarTextMath: false })
      .use(remarkCallout)
      .use(remarkUnwrapImages);

    const ast = processor.parse(markdown);
    return processor.runSync(ast);
  }

  async fromBlockSnapshot({
    snapshot,
    assets,
  }: FromBlockSnapshotPayload): Promise<FromBlockSnapshotResult<Markdown>> {
    const root: Root = {
      type: 'root',
      children: [],
    };
    const { ast, assetsIds } = await this._traverseSnapshot(
      snapshot,
      root,
      assets
    );
    return {
      file: this._astToMarkdown(ast),
      assetsIds,
    };
  }

  async fromDocSnapshot({
    snapshot,
    assets,
  }: FromDocSnapshotPayload): Promise<FromDocSnapshotResult<Markdown>> {
    let buffer = '';
    const { file, assetsIds } = await this.fromBlockSnapshot({
      snapshot: snapshot.blocks,
      assets,
    });
    buffer += file;
    return {
      file: buffer,
      assetsIds,
    };
  }

  async fromSliceSnapshot({
    snapshot,
    assets,
    template,
  }: FromSliceSnapshotPayload): Promise<FromSliceSnapshotResult<Markdown>> {
    let buffer = '';
    const sliceAssetsIds: string[] = [];
    for (const contentSlice of snapshot.content) {
      const root: Root = {
        type: 'root',
        children: [],
      };
      const { ast, assetsIds } = await this._traverseSnapshot(
        contentSlice,
        root,
        assets,
        template
      );
      sliceAssetsIds.push(...assetsIds);
      buffer += this._astToMarkdown(ast);
    }
    const markdown =
      buffer.match(/\n/g)?.length === 1 ? buffer.trimEnd() : buffer;
    return {
      file: markdown,
      assetsIds: sliceAssetsIds,
    };
  }

  async toBlockSnapshot(
    payload: ToBlockSnapshotPayload<Markdown>
  ): Promise<BlockSnapshot> {
    const markdownFile = this.preprocessorManager.process(
      'block',
      payload.file
    );
    const markdownAst = this._markdownToAst(markdownFile);
    const blockSnapshotRoot = {
      type: 'block',
      id: nanoid(),
      flavour: 'affine:note',
      props: {
        xywh: '[0,0,800,95]',
        background: DefaultTheme.noteBackgrounColor,
        index: 'a0',
        hidden: false,
        displayMode: NoteDisplayMode.DocAndEdgeless,
      },
      children: [],
    };
    return this._traverseMarkdown(
      markdownAst,
      blockSnapshotRoot as BlockSnapshot,
      payload.assets
    );
  }

  async toDocSnapshot(
    payload: ToDocSnapshotPayload<Markdown>
  ): Promise<DocSnapshot> {
    const markdownFile = this.preprocessorManager.process('doc', payload.file);
    const markdownAst = this._markdownToAst(markdownFile);
    const blockSnapshotRoot = {
      type: 'block',
      id: nanoid(),
      flavour: 'affine:note',
      props: {
        xywh: '[0,0,800,95]',
        background: DefaultTheme.noteBackgrounColor,
        index: 'a0',
        hidden: false,
        displayMode: NoteDisplayMode.DocAndEdgeless,
      },
      children: [],
    };
    return {
      type: 'page',
      meta: {
        id: nanoid(),
        title: 'Untitled',
        createDate: Date.now(),
        tags: [],
      },
      blocks: {
        type: 'block',
        id: nanoid(),
        flavour: 'affine:page',
        props: {
          title: {
            '$blocksuite:internal:text$': true,
            delta: [
              {
                insert: 'Untitled',
              },
            ],
          },
        },
        children: [
          {
            type: 'block',
            id: nanoid(),
            flavour: 'affine:surface',
            props: {
              elements: {},
            },
            children: [],
          },
          await this._traverseMarkdown(
            markdownAst,
            blockSnapshotRoot as BlockSnapshot,
            payload.assets
          ),
        ],
      },
    };
  }

  async toSliceSnapshot(
    payload: MarkdownToSliceSnapshotPayload & { template?: boolean }
  ): Promise<SliceSnapshot | null> {
    const markdownFile = this.preprocessorManager.process(
      'slice',
      payload.file
    );
    const markdownAst = this._markdownToAst(markdownFile);
    const blockSnapshotRoot = {
      type: 'block',
      id: nanoid(),
      flavour: 'affine:note',
      props: {
        xywh: '[0,0,800,95]',
        background: DefaultTheme.noteBackgrounColor,
        index: 'a0',
        hidden: false,
        displayMode: NoteDisplayMode.DocAndEdgeless,
      },
      children: [],
    } as BlockSnapshot;
    const contentSlice = (await this._traverseMarkdown(
      markdownAst,
      blockSnapshotRoot,
      payload.assets,
      payload?.template
    )) as BlockSnapshot;
    if (contentSlice.children.length === 0) {
      return null;
    }
    return {
      type: 'slice',
      content: [contentSlice],
      workspaceId: payload.workspaceId,
      pageId: payload.pageId,
    };
  }

  async toBlockSnapshotInSliceStruct(
    payload: MarkdownToSliceSnapshotPayload & { template?: boolean }
  ): Promise<SliceSnapshot | null> {
    const markdownFile = this.preprocessorManager.process(
      'block',
      payload.file
    );
    const markdownAst = this._markdownToAst(markdownFile);
    const blockSnapshotRoot = {
      type: 'block',
      id: nanoid(),
      flavour: 'affine:note',
      props: {
        xywh: '[0,0,800,95]',
        background: DefaultTheme.noteBackgrounColor,
        index: 'a0',
        hidden: false,
        displayMode: NoteDisplayMode.DocAndEdgeless,
      },
      children: [],
    } as BlockSnapshot;
    const contentSlice = (await this._traverseMarkdown(
      markdownAst,
      blockSnapshotRoot,
      payload.assets,
      payload?.template
    )) as BlockSnapshot;
    if (contentSlice.children.length === 0) {
      return null;
    }
    return {
      type: 'slice',
      content: [contentSlice],
      workspaceId: payload.workspaceId,
      pageId: payload.pageId,
    };
  }

  async fromBlock2LineMap({
    snapshot,
    assets,
    blockList,
  }: FromDocSnapshotPayload & { blockList: string[] }): Promise<{
    blockMap: BlockMarkdownMap[];
  }> {
    const { file, assetsIds, blockMap } =
      await this.fromBlockSnapshotWithLineMap({
        snapshot: snapshot.blocks,
        assets,
      });
    const block2LineMap = blockList
      .map(blockId => {
        return blockMap.find(map => map.blockId === blockId);
      })
      .filter((item): item is BlockMarkdownMap => item !== undefined);

    return { blockMap: block2LineMap };
  }

  async fromLine2BlockMap({
    snapshot,
    assets,
    lineList,
  }: FromDocSnapshotPayload & { lineList: number[] }): Promise<{
    blockMap: BlockMarkdownMap[];
  }> {
    const { file, assetsIds, blockMap } =
      await this.fromBlockSnapshotWithLineMap({
        snapshot: snapshot.blocks,
        assets,
      });
    const line2BlockMap = lineList
      .map(line => {
        return blockMap.find(map => map.startLine === line);
      })
      .filter((item): item is BlockMarkdownMap => item !== undefined);

    return { blockMap: line2BlockMap };
  }

  // 完全重写 fromBlockSnapshotWithLineMap 方法
  async fromBlockSnapshotWithLineMap({
    snapshot,
    assets,
  }: FromBlockSnapshotPayload): Promise<
    FromBlockSnapshotResult<Markdown> & { blockMap: BlockMarkdownMap[] }
  > {
    // 第一步：使用标准流程生成干净的 markdown
    const root: Root = { type: 'root', children: [] };
    const { ast, assetsIds } = await this._traverseSnapshot(
      snapshot,
      root,
      assets
    );
    const cleanMarkdown = this._astToMarkdown(ast);

    // 第二步：递归解析 blocks 和 markdown 行来构建精确映射
    const blockMap = await this._buildPreciseBlockMap(
      snapshot,
      cleanMarkdown,
      assets
    );

    return {
      file: cleanMarkdown,
      assetsIds,
      blockMap,
    };
  }

  // 新增方法：格式化 Markdown（Markdown -> Blocks(slice) -> Markdown）
  async formatMarkdown(file: Markdown): Promise<string> {
    const slice = await this.toBlockSnapshotInSliceStruct({
      file,
      assets: this.job.assetsManager,
      workspaceId: nanoid(),
      pageId: nanoid(),
      template: true,
    });

    if (!slice) {
      return '';
    }

    const { file: formatted } = await this.fromSliceSnapshot({
      snapshot: slice,
      assets: this.job.assetsManager,
      template: true,
    });

    return formatted;

    // Post-process: restore image blocks by sequence
    // Step 1: collect original image blocks in order
    // const originalLines = file.split('\n');
    // type OriginalImageBlock = { imageLine: string; captionLine?: string };
    // const originalImageBlocks: OriginalImageBlock[] = [];
    // const imageLineRe = /^!\[[^\]]*\]\([^)]+\)\s*$/;
    // const italicLineRe = /^\*.*\*\s*$/;
    // for (let i = 0; i < originalLines.length; i++) {
    //   const line = originalLines[i];
    //   if (imageLineRe.test(line)) {
    //     let captionLine: string | undefined;
    //     for (let j = i + 1; j < originalLines.length; j++) {
    //       const next = originalLines[j];
    //       if (next.trim() === '') continue;
    //       if (italicLineRe.test(next)) captionLine = next;
    //       break;
    //     }
    //     originalImageBlocks.push({ imageLine: line, captionLine });
    //   }
    // }

    // if (originalImageBlocks.length === 0) {
    //   return formatted;
    // }

    // // Step 2: walk the formatted output and replace caption + blank + asset image with original sequence
    // const outLines = formatted.split('\n');
    // const assetImageRe = /^!\[[^\]]*\]\(assets\/[^)]+\)\s*$/;
    // const result: string[] = [];
    // for (let i = 0; i < outLines.length; i++) {
    //   const line = outLines[i];
    //   const next = i + 1 < outLines.length ? outLines[i + 1] : '';
    //   const next2 = i + 2 < outLines.length ? outLines[i + 2] : '';
    //   const isCaption = italicLineRe.test(line.trim());
    //   const isBlank = next.trim() === '';
    //   const isAssetImg = assetImageRe.test(next2.trim());
    //   if (
    //     isCaption &&
    //     isBlank &&
    //     isAssetImg &&
    //     originalImageBlocks.length > 0
    //   ) {
    //     const orig = originalImageBlocks.shift()!;
    //     // Replace triplet with original image then original caption (if any)
    //     result.push(orig.imageLine);
    //     if (orig.captionLine) {
    //       result.push(orig.captionLine);
    //     }
    //     i += 2; // skip the next two lines (blank + image)
    //     continue;
    //   } else if (isAssetImg) {
    //     // 资产图片单独处理，不添加到结果中
    //     const orig = originalImageBlocks.shift()!;
    //     result.push(orig.imageLine);
    //     i += 2;
    //     continue;
    //   }

    //   result.push(line);
    // }

    // return result.join('\n');
  }

  // 新增：精确构建块映射的主函数
  private async _buildPreciseBlockMap(
    snapshot: BlockSnapshot,
    finalMarkdown: string,
    assets?: AssetsManager
  ): Promise<BlockMarkdownMap[]> {
    const finalLines = finalMarkdown.split('\n');
    const blockMap: BlockMarkdownMap[] = [];

    // 获取所有内容块的层级结构
    const contentBlocks = this._buildBlockHierarchy(snapshot);

    // 检测是否存在 Title 不存在，则添加默认标题，防止解析失败
    const hasTitleBlock = !!(
      (snapshot as any)?.props?.title?.delta &&
      Array.isArray((snapshot as any).props.title.delta) &&
      (snapshot as any).props.title.delta.length > 0
    );

    if (!hasTitleBlock) {
      finalLines.unshift('# Untitled', '');
    }

    // 跳过第一行（doc title）和可能的空行
    let currentLineIndex = 0;
    // while (currentLineIndex < finalLines.length && finalLines[currentLineIndex].trim() === '') {
    //   currentLineIndex++;
    // }

    // 递归处理每个顶级块
    for (const blockInfo of contentBlocks) {
      const result = await this._processBlockRecursively(
        blockInfo,
        finalLines,
        currentLineIndex,
        assets
      );

      if (result) {
        blockMap.push(...result.blockMaps);
        currentLineIndex = result.nextLineIndex;
      }
    }

    return blockMap;
  }

  // 修复：构建块的层级结构
  private _buildBlockHierarchy(snapshot: BlockSnapshot): BlockHierarchy[] {
    const blocks: BlockHierarchy[] = [];

    const traverse = (node: BlockSnapshot, level: number = 0): void => {
      // 如果是 page 或 note，递归处理其子元素
      if (node.flavour === 'affine:page' || node.flavour === 'affine:note') {
        if (node.children && Array.isArray(node.children)) {
          for (const child of node.children) {
            traverse(child as BlockSnapshot, level);
          }
        }
      } else {
        // 先收集子层级结构
        const children: BlockHierarchy[] = [];

        if (node.children && Array.isArray(node.children)) {
          for (const child of node.children) {
            children.push({
              block: child as BlockSnapshot,
              children: this._getChildrenHierarchy(
                child as BlockSnapshot,
                level + 1
              ),
              level: level + 1,
            });
          }
        }

        // 将当前块添加到结果中（在子块处理之前）
        blocks.push({
          block: node,
          children,
          level,
        });
      }
    };

    if (Array.isArray(snapshot)) {
      for (const item of snapshot) {
        traverse(item);
      }
    } else {
      traverse(snapshot);
    }

    return blocks;
  }

  // 完全重写：获取子块层级结构（修复顺序问题）
  private _getChildrenHierarchy(
    block: BlockSnapshot,
    level: number
  ): BlockHierarchy[] {
    const children: BlockHierarchy[] = [];

    if (block.children && Array.isArray(block.children)) {
      for (const child of block.children) {
        children.push({
          block: child as BlockSnapshot,
          children: this._getChildrenHierarchy(
            child as BlockSnapshot,
            level + 1
          ),
          level: level + 1,
        });
      }
    }

    return children;
  }

  // 重写：递归处理块及其子块（修复处理逻辑）
  private async _processBlockRecursively(
    blockInfo: BlockHierarchy,
    finalLines: string[],
    startLineIndex: number,
    assets?: AssetsManager
  ): Promise<{ blockMaps: BlockMarkdownMap[]; nextLineIndex: number } | null> {
    const { block, children } = blockInfo;
    const blockId = String(block.id ?? '');
    const { flavour } = block;

    if (!blockId) return null;

    // surface block 单独处理
    if (flavour === 'affine:surface') {
      return {
        blockMaps: [
          {
            blockId,
            startLine: startLineIndex + 1,
            endLine: startLineIndex + 1,
            markdown: '',
          },
        ],
        nextLineIndex: startLineIndex + 1,
      };
    }

    // 为当前块生成 markdown（包含子块，用于确定整个范围）
    const fullBlockMarkdown = await this._generateFullBlockMarkdown(
      block,
      assets
    );
    const fullLines = fullBlockMarkdown
      .split('\n')
      .filter(line => line.trim() !== '');

    if (fullLines.length === 0) {
      // 空块处理, 空块包含当前行和下一行 emptyLineIndex
      const emptyLineIndex = this._findNextEmptyLine(
        finalLines,
        startLineIndex
      );
      if (emptyLineIndex !== -1) {
        return {
          blockMaps: [
            {
              blockId,
              startLine: emptyLineIndex + 2,
              endLine: emptyLineIndex + 2,
              markdown: '',
            },
          ],
          nextLineIndex: emptyLineIndex + 2,
        };
      }
      return null;
    }

    // 在最终 markdown 中查找当前块的起始位置
    const matchResult = this._findBlockStartInLines(
      fullLines[0],
      finalLines,
      startLineIndex
    );
    if (!matchResult) {
      return null;
    }

    const blockStartLine = matchResult.lineIndex + 1; // 转为 1-based
    const allBlockMaps: BlockMarkdownMap[] = [];

    // 如果没有子块，直接处理当前块
    if (children.length === 0) {
      // 找到当前块的结束位置
      let blockEndLine = blockStartLine;
      let matchedLines = 0;

      for (
        let i = matchResult.lineIndex;
        i < finalLines.length && matchedLines < fullLines.length;
        i++
      ) {
        if (finalLines[i].trim() !== '') {
          matchedLines++;
        }
        blockEndLine = i + 1; // 转为 1-based
      }

      allBlockMaps.push({
        blockId,
        startLine: blockStartLine,
        endLine: blockEndLine,
        markdown: finalLines.slice(blockStartLine - 1, blockEndLine).join('\n'),
      });

      return {
        blockMaps: allBlockMaps,
        nextLineIndex: blockEndLine,
      };
    }

    // 有子块的情况：先计算完整范围，再处理子块
    let blockEndLine = blockStartLine;
    let matchedLines = 0;

    for (
      let i = matchResult.lineIndex;
      i < finalLines.length && matchedLines < fullLines.length;
      i++
    ) {
      if (finalLines[i].trim() !== '') {
        matchedLines++;
      }
      blockEndLine = i + 1; // 转为 1-based
    }

    // 处理子块：从父块开始位置查找
    let childProcessingLine = matchResult.lineIndex;

    // 为当前块生成不包含子块的内容，确定自身内容的结束位置
    const ownBlockMarkdown = await this._generateCleanBlockMarkdown(
      block,
      assets
    );
    const ownLines = ownBlockMarkdown
      .split('\n')
      .filter(line => line.trim() !== '');

    let ownEndLine = blockStartLine;
    if (ownLines.length > 0) {
      let ownMatchedLines = 0;
      for (
        let i = matchResult.lineIndex;
        i < finalLines.length && ownMatchedLines < ownLines.length;
        i++
      ) {
        if (finalLines[i].trim() !== '') {
          ownMatchedLines++;
        }
        ownEndLine = i + 1;
      }
      childProcessingLine = ownEndLine;
    }

    // 递归处理所有子块
    for (const childInfo of children) {
      const childResult = await this._processBlockRecursively(
        childInfo,
        finalLines,
        childProcessingLine,
        assets
      );

      if (childResult) {
        allBlockMaps.push(...childResult.blockMaps);
        childProcessingLine = childResult.nextLineIndex;
      }
    }

    // 当前块的范围是完整的范围（包括其所有子块）
    const parentBlockMap: BlockMarkdownMap = {
      blockId,
      startLine: blockStartLine,
      endLine: blockEndLine, // 使用计算出的完整范围
      markdown: finalLines.slice(blockStartLine - 1, blockEndLine).join('\n'),
    };

    // 将父块放在最前面
    allBlockMaps.unshift(parentBlockMap);

    return {
      blockMaps: allBlockMaps,
      nextLineIndex: blockEndLine,
    };
  }

  // 新增：生成完整的块 markdown（包含子块）
  private async _generateFullBlockMarkdown(
    block: BlockSnapshot,
    assets?: AssetsManager
  ): Promise<string> {
    const tempPageSnapshot: BlockSnapshot = {
      type: 'block',
      id: 'temp-page',
      flavour: 'affine:page',
      version: 2,
      props: { title: { '$blocksuite:internal:text$': true, delta: [] } },
      children: [
        {
          type: 'block',
          id: 'temp-note',
          flavour: 'affine:note',
          version: 1,
          props: {},
          children: [block], // 保留完整的子结构
        },
      ],
    };

    const tempRoot: Root = { type: 'root', children: [] };
    const { ast } = await this._traverseSnapshot(
      tempPageSnapshot,
      tempRoot,
      assets
    );
    return this._astToMarkdown(ast);
  }

  // 新增：在行中查找块的起始位置
  private _findBlockStartInLines(
    targetLine: string,
    finalLines: string[],
    startIndex: number
  ): { lineIndex: number } | null {
    const cleanTarget = targetLine.trim();

    for (let i = startIndex; i < finalLines.length; i++) {
      const cleanLine = finalLines[i].trim();
      if (cleanLine === cleanTarget) {
        return { lineIndex: i };
      }

      // 兼容处理：列表格式的兼容匹配
      if (this._isListLineCompatible(cleanTarget, cleanLine)) {
        return { lineIndex: i };
      }
    }

    return null;
  }

  // 修改：通用列表兼容性检查（包括任务列表、有序列表、无序列表）
  private _isListLineCompatible(target: string, line: string): boolean {
    // 提取列表内容（忽略符号、序号等前缀）
    const targetContent = this._extractListContent(target);
    const lineContent = this._extractListContent(line);

    return (
      targetContent !== null &&
      lineContent !== null &&
      targetContent.indent === lineContent.indent &&
      targetContent.content === lineContent.content &&
      targetContent.checkbox === lineContent.checkbox
    );
  }

  // 新增：提取列表内容
  private _extractListContent(line: string): {
    indent: string;
    content: string;
    checkbox?: string;
  } | null {
    // 匹配各种列表格式
    const patterns = [
      // 任务列表: "  * [x] content" 或 "  - [ ] content"
      /^(\s*)([*-]) (\[[ x\]]) (.+)$/,
      // 有序列表: "  1. content" 或 "  123. content"
      /^(\s*)(\d+)\. (.+)$/,
      // 无序列表: "  * content" 或 "  - content"
      /^(\s*)([*-]) (.+)$/,
    ];

    for (const pattern of patterns) {
      const match = line.match(pattern);
      if (match) {
        if (pattern.source.includes('\\[')) {
          // 任务列表
          const [, indent, , checkbox, content] = match;
          return { indent, content, checkbox };
        } else {
          // 无序列表
          const [, indent, , content] = match;
          return { indent, content };
        }
        // 解决代码报错注释掉底下这行
        // else if (pattern.source.includes('\\d+')) {
        //   // 有序列表 - 忽略序号，只比较内容
        //   const [, indent, , content] = match;
        //   return { indent, content };
        // } else {
        //   // 无序列表
        //   const [, indent, , content] = match;
        //   return { indent, content };
        // }
      }
    }

    return null;
  }

  // 新增：查找下一个空行
  private _findNextEmptyLine(finalLines: string[], startIndex: number): number {
    for (let i = startIndex; i < finalLines.length; i++) {
      if (finalLines[i].trim() === '') {
        return i;
      }
    }
    return -1;
  }

  // 新增：生成干净的块 markdown（不包括子块）
  private async _generateCleanBlockMarkdown(
    block: BlockSnapshot,
    assets?: AssetsManager
  ): Promise<string> {
    // 创建一个临时块，不包含子元素
    const tempBlock: BlockSnapshot = {
      ...block,
      children: [], // 移除子元素
    };

    const tempPageSnapshot: BlockSnapshot = {
      type: 'block',
      id: 'temp-page',
      flavour: 'affine:page',
      version: 2,
      props: { title: { '$blocksuite:internal:text$': true, delta: [] } },
      children: [
        {
          type: 'block',
          id: 'temp-note',
          flavour: 'affine:note',
          version: 1,
          props: {},
          children: [tempBlock],
        },
      ],
    };

    const tempRoot: Root = { type: 'root', children: [] };
    const { ast } = await this._traverseSnapshot(
      tempPageSnapshot,
      tempRoot,
      assets
    );
    return this._astToMarkdown(ast);
  }

  // 避免在下划线前出现反斜杠“翻倍”
  private _normalizeEscape(markdown: string): string {
    return markdown.replace(/\\\\+/g, '\\');
  }
}

export const MarkdownAdapterFactoryIdentifier =
  AdapterFactoryIdentifier('Markdown');

export const MarkdownAdapterFactoryExtension: ExtensionType = {
  setup: di => {
    di.addImpl(MarkdownAdapterFactoryIdentifier, provider => ({
      get: (job: Transformer) => new MarkdownAdapter(job, provider),
    }));
  },
};
